import { ERROR_CODES, type ApiErrorBody, type AssignmentDto, type ErrorCode } from '@betreuung/shared';

/**
 * Thin fetch wrapper. The API answers a single documented error shape, so all
 * failures arrive as an `ApiError` and the UI can branch on `code` instead of
 * parsing messages.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Network-level failure: server unreachable, request aborted by the browser. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Keine Verbindung zum Server');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
  } catch (error) {
    throw new NetworkError(error);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text.length > 0 ? JSON.parse(text) : null;

  if (!response.ok) {
    const body = payload as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      body?.error?.code ?? ERROR_CODES.INTERNAL,
      body?.error?.message ?? 'Anfrage fehlgeschlagen',
      body?.error?.details,
    );
  }

  return payload as T;
}

export function jsonBody(value: unknown): RequestInit {
  return { method: 'POST', body: JSON.stringify(value) };
}

/** The range collides with an existing assignment; carries that assignment. */
export function overlapConflict(error: unknown): AssignmentDto | null {
  if (!(error instanceof ApiError) || error.code !== ERROR_CODES.CONFLICT_OVERLAP) return null;
  const details = error.details as { existing?: AssignmentDto } | undefined;
  return details?.existing ?? null;
}

/**
 * The assignment changed under us. Returns `{ current: null }` when it was
 * deleted meanwhile, so callers can tell "changed" from "gone".
 */
export function staleConflict(error: unknown): { current: AssignmentDto | null } | null {
  if (!(error instanceof ApiError) || error.code !== ERROR_CODES.CONFLICT_STALE) return null;
  const details = error.details as { current?: AssignmentDto | null } | undefined;
  return { current: details?.current ?? null };
}

export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.code === ERROR_CODES.UNAUTHORIZED;
}
